import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Container, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import _ from "lodash";
import { parse, stringify } from 'query-string';

import DeleteIcon from 'mdi-react/DeleteIcon';
import PencilIcon from 'mdi-react/PencilIcon';
import ThumbUpOutlineIcon from 'mdi-react/ThumbUpOutlineIcon';
import CloseCircleOutlineIcon from 'mdi-react/CloseCircleOutlineIcon';

import Roles from '../../../config/roles';
import { getProjectsFetchingAction } from '../../../redux/actions/projectsActions';
import {
  deleteProject,
  putActivateProject,
  putDeactivateProject,
} from '../../../utils/api';

import { TableConfig } from './components/ProjectsTableColumns';
import DataReactTable from '../../Tables/DataTable/components/DataReactTable';
import ConfirmModal from '../../../shared/components/customComponents/CustomConfirmModal/ConfirmModal';
import CustomModal from '../../../shared/components/customComponents/CustomModal/CustomModal';
import ProjectsFilterForm from './components/ProjectsFilterForm/ProjectsFilterForm';

const Developers = ({p}) => {
  const [show, setShow] = useState(false);

  return <>
    {
      _.map(_.slice(p, 0, show ? p?.length : 4), d => {
        return (
          <>
            {d?.first_name} {d?.last_name} <br/>
          </>
        )
      })
    }
    {
      p?.length > 4 ?
        (<button
          onClick={() => setShow(!show)}
          style={{padding: "2px 5px", display: "block", background: "#fff", border: "1px solid #efefef"}}
        > {show ? "hide" : "..."}
        </button>) : null
    }
  </>
}

const TableButton = ({ isActive, setActivateId, id, setActivateModal, setDeactivateId, setDeactivateModal }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getMetaData = () => {
    const obj = {
      btnLabel: "",
      btnColor: ""
    }
    if (isActive === "INPROGRESS") {
      obj.btnLabel = "Activated";
      obj.btnColor = "success";
    }
    if (isHovered && isActive === "INPROGRESS") {
      obj.btnLabel = "Deactivate";
      obj.btnColor = "danger";
    }

    if (isActive === "ONHOLD" || isActive === "NEW") {
      obj.btnLabel = "Deactivated";
      obj.btnColor = "danger";
    }
    if ((isActive === "ONHOLD" && isHovered) || (isActive === "NEW" && isHovered)) {
      obj.btnLabel = "Activate";
      obj.btnColor = "success";
    }

    return obj;
  }

  const { btnLabel, btnColor } = getMetaData();

  return (
    <Button
      className="icon table-button mt-1"
      size="sm"
      color={btnColor}
      style={{ padding: "5px 10px", minWidth: "121px", width: "100%" }}
      onMouseOver={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        if (isActive === "ONHOLD" || isActive === "NEW") {
          setActivateId(id);
          setActivateModal(true);
        } else {
          setDeactivateId(id);
          setDeactivateModal(true);
        }
      }}
    >
      {btnColor === "success" ? (<ThumbUpOutlineIcon />) : (<CloseCircleOutlineIcon />)}
      {btnLabel}
    </Button>
  )
}

const Projects = () => {
  const { t } = useTranslation('common');
  const {projects, errors, totalRecords} = useSelector(state => state.projects);
  const authUser = useSelector(state => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const parsedQuery = parse(location.search);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [query, setQuery] = useState({start: 0, limit: 10, search_query: ''});
  const [currentPage, setCurrentPage] = useState(+parsedQuery?.start || 1);
  const [pageLimit, setPageLimit] = useState(+parsedQuery?.limit || 10);
  const [activateId, setActivateId] = useState(null);
  const [activateModal, setActivateModal] = useState(false);
  const [deactivateId, setDeactivateId] = useState(null);
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [messageActivateError, setMessageActivateError] = useState('');
  const [failedActivateModal, setFailedActivateModal] = useState(false);
  const [successActivateModal, setSuccessActivateModal] = useState(false);
  const [messageDeactivateError, setMessageDeactivateError] = useState('');
  const [failedDeactivateModal, setFailedDeactivateModal] = useState(false);
  const [successDeactivateModal, setSuccessDeactivateModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [failedDeleteModal, setFailedDeleteModal] = useState(false);
  const [successDeletedModal, setSuccessDeletedModal] = useState(false);
  const columns = TableConfig().columns;
  const [order, setOrder] = useState({name: 'id', desc: 'desc'});

  useEffect(() => {
    const updatedQuery = {
      ...query,
      start: (currentPage - 1) * pageLimit,
      limit: pageLimit
    };

    navigate(`${location.pathname}?${stringify({start: currentPage, limit: pageLimit}, {arrayFormat: 'comma'})}`);
    dispatch(getProjectsFetchingAction(updatedQuery, order));
  }, [currentPage, pageLimit, query, dispatch, refreshCounter, location.pathname, navigate, order]);

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangePageSize = (newSize) => {
    setCurrentPage(1);
    setPageLimit(newSize);
  };

  const handleActivateProject = () => {
    putActivateProject(activateId, authUser.accessToken).then(res => {
      if(res.data.success) {
        setSuccessActivateModal(!successActivateModal);
        setRefreshCounter(refreshCounter + 1);
      } else {
        setMessageActivateError(res.data.errors[0].message);
        setFailedActivateModal(!failedActivateModal);
      }
    })
  }

  const handleDeactivateProject = () => {
    putDeactivateProject(deactivateId, authUser.accessToken).then(res => {
      if(res.data.success) {
        setSuccessDeactivateModal(!successDeactivateModal);
        setRefreshCounter(refreshCounter + 1);
      } else {
        setMessageDeactivateError(res.data.errors[0].message);
        setFailedDeactivateModal(!failedDeactivateModal);
      }
    })
  }

  const handleDeleteProject = () => {
    deleteProject(deleteId, authUser.accessToken).then(res => {
      if(res.data.success) {
        setSuccessDeletedModal(!successDeletedModal);
      } else {
        setMessageError(res.data.errors[0].message);
        setFailedDeleteModal(!failedDeleteModal);
      }
    })
  }

  const applyFilterForm = (value) => {
    return setQuery({...query, search_query: value?.search});
  }

  const resetFilterForm = () => {
    return setQuery({...query, search_query: ''});
  }

  const checkHiddenColumns = () => {
    if(['CLIENT'].includes(authUser.role)) {
      return ['cost', 'clients', 'actions'];
    }

    return [];
  }

  return (
    <Container>
      <Row>
        {
          (authUser.role === Roles.ADMIN || authUser.role === Roles.SALES) && (
            <Col className="col-12 d-flex justify-content-end">
              <button className="btn btn-success" onClick={() => {
                navigate("/management/projects/add", {
                  state: {
                    prevQuery: [{
                      query: location.search,
                      path: location.pathname,
                    }]
                  }
                });
              }}>{t('projects.create_button')}</button>
            </Col>
          )
        }
        <Col className="col-12 pl-0 pr-0">
          <ProjectsFilterForm
            onSubmit={applyFilterForm}
            resetForm={resetFilterForm}
          />
        </Col>
        <Col className="col-12">
          <DataReactTable
            tableName={"Projects"}
            classList={"table-projects"}
            sortTable
            search={false}
            pagination={{
              onChangePageSize: handleChangePageSize,
              onChangePage: handleChangePage,
              currentPage,
              totalRecords,
              pageLimit
            }}
            onSortChange={(sortBy) => {
              const { id, desc } = sortBy[0] || {};
              const newOrder = { name: id, desc: desc ? 'desc' : 'asc' };
              if (id && (order.name !== newOrder.name || order.desc !== newOrder.desc)) {
                setOrder(newOrder);
              }
            }}
            emptyStateMessage={t('empty_states.projects')}
            reactTableData={{tableHeaderData: _.filter(columns, c => !checkHiddenColumns().includes(c.accessor)),
            tableRowsData: projects?.map(p => {
              return {
                ...p,
                jira_code: p.jira,
                total_logged_hours: p.total_logged,
                total_paid_hours: p.total_paid,
                total_approved_hours: p.total_approved,
                developers: <Developers p={p.developers} />,
                clients: p?.clients?.map(c => {
                  return (
                    <span style={{whiteSpace: "nowrap"}}>
                      {c.first_name + " " + c.last_name} <br />
                    </span>
                  )
                }),
                actions: <div className={"tableActions"}>
                  <ReactTooltip id="deleteIcon" type="dark">
                    <span style={{ color: 'white' }}>
                      {t("reactTooltips.delete")}
                    </span>
                  </ReactTooltip>
                  <button
                    data-tip
                    data-for="deleteIcon"
                    style={{border: 0, background: 'transparent'}}>
                    <DeleteIcon
                      onClick={() => {
                        setDeleteId(p.id)
                        setConfirmModal(true)}}
                      size={18}
                      color="#b1c3c8" />
                  </button>
                  {
                    authUser.role === Roles.ADMIN || (authUser.role === Roles.SALES && authUser.userId === p.is_sales) ? (<>
                      <ReactTooltip id="editIcon" type="dark">
                        <span style={{ color: 'white' }}>
                          {t("reactTooltips.edit")}
                        </span>
                      </ReactTooltip>
                      <button
                        data-tip
                        data-for="editIcon"
                        style={{border: 0, background: 'transparent'}}
                        onClick={() => {
                          navigate(`/management/projects/${p.id}/edit`, {
                            state: {
                              prevQuery: [{
                                query: location.search,
                                path: location.pathname,
                              }]
                            }
                          });
                        }}
                      >
                        <PencilIcon color="#b1c3c8" size={18} />
                      </button>
                    </>) : null
                  }

                  <TableButton
                    isActive={p.status}
                    id={p.id}
                    setActivateId={setActivateId}
                    setActivateModal={setActivateModal}
                    setDeactivateId={setDeactivateId}
                    setDeactivateModal={setDeactivateModal}
                  />
                </div>
              }
             })}}
          />

          {
            errors ? (<div>{errors}</div>) : null
          }

          <ConfirmModal
            color="primary"
            btn="Default"
            message={`Are you sure you want to activate project #${activateId}?`}
            toggle={() => setActivateModal(!activateModal)}
            modal={activateModal}
            onNext={handleActivateProject}
          />
          <CustomModal
            successModal={failedActivateModal}
            toggleCloseModal={() => setFailedActivateModal(!failedActivateModal)}
            textModal={messageActivateError}
            color={"danger"}
          />
          <ConfirmModal
            color="danger"
            btn="Default"
            message={`Are you sure you want to deactivate project #${deactivateId}?`}
            toggle={() => setDeactivateModal(!deactivateModal)}
            modal={deactivateModal}
            onNext={handleDeactivateProject}
          />
          <CustomModal
            successModal={failedDeactivateModal}
            toggleCloseModal={() => setFailedDeactivateModal(!failedDeactivateModal)}
            textModal={messageDeactivateError}
            color={"danger"}
          />
          <ConfirmModal
            color="danger"
            btn="Default"
            message={`Are you sure you want to delete project #${deleteId}?`}
            toggle={() => setConfirmModal(!confirmModal)}
            modal={confirmModal}
            onNext={handleDeleteProject}
          />
          <CustomModal
            successModal={failedDeleteModal}
            toggleCloseModal={() => setFailedDeleteModal(!failedDeleteModal)}
            textModal={messageError}
            color={"danger"}
          />
          <CustomModal
            successModal={successDeletedModal}
            toggleCloseModal={() => {
              setSuccessDeletedModal(!successDeletedModal);
              setRefreshCounter(refreshCounter + 1);
            }}
            textModal={`Record #${deleteId} has been deleted!`}
            color={"success"}
          />
        </Col>
      </Row>
    </Container>
  )
};

export default Projects;
